import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { getAuthToken, resolveImageUrl } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Car, Truck, History, AlertCircle, XCircle, ShieldCheck, Gift, Edit2, MapPin, UserCircle, Phone, Mail, FileText, Loader2, Save, CreditCard, Star as StarIcon, BarChart3, Coins, Hourglass, Zap, LifeBuoy, MessageCircle, BellOff, Share2, QrCode } from "lucide-react";
import { AddRideDialog, AddVehicleDialog, EditRideDialog, EditVehicleDialog } from "../components/driver/DriverForms";
import DriverBookingCard from "../components/driver/DriverBookingCard";
import PackBookingsReport from "../components/PackBookingsReport";
import DriverStats from "../components/driver/DriverStats";
import TFTHistory from "../components/TFTHistory";
import TFTRecharge from "../components/TFTRecharge";
import ReferralSection from "../components/ReferralSection";
import { SUPPORT_PHONE, SUPPORT_WHATSAPP_URL } from "../lib/support";
import { shareApp } from "../lib/share";
import ShareAppDialog from "../components/ShareAppDialog";
import ReferralStatsCard from "../components/ReferralStatsCard";
import ShareQrDialog from "../components/ShareQrDialog";
import ReviewBadge from "../components/ReviewBadge";
import { VehicleStatusBadge, VehicleStatusStrip } from "../components/VehicleStatusBadge";
import { useWebSocketNotifications } from "../hooks/useWebSocket";
import { usePushNotifications } from "../hooks/usePushNotifications";
import NotificationsActivateButton from "../components/NotificationsActivateButton";
import DriverPushActivationBanner from "../components/driver/DriverPushActivationBanner";
import NotificationSoundSettings from "../components/NotificationSoundSettings";
import DriverOnlineToggle from "../components/driver/DriverOnlineToggle";
import DriverVisibilityToggle from "../components/driver/DriverVisibilityToggle";
import ExpressCourseCard from "../components/ExpressCourseCard";
import ExpressPendingQueue from "../components/ExpressPendingQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthImgUrl(docPath) {
  if (!docPath) return null;
  const token = getAuthToken();
  return `${BACKEND_URL}/api/files/${docPath}?token=${token}`;
}

function log(...args) {
  if (process.env.NODE_ENV === "development") console.error(...args);
}

export default function DriverDashboard() {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState("rides");
  const [myRides, setMyRides] = useState([]);
  const [myVehicles, setMyVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showRideForm, setShowRideForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [trackingBookingId, setTrackingBookingId] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [editRide, setEditRide] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const watchIdRef = useRef(null);

  const { notifications, unreadCount } = useWebSocketNotifications(user?.id);
  usePushNotifications(user?.id, user?.role);

  const fetchMyRides = useCallback(async () => {
    try { const { data } = await api.get("/rides/driver/me"); setMyRides(data); } catch (err) { log("Failed to fetch rides:", err.message); }
  }, []);
  const fetchMyVehicles = useCallback(async () => {
    try { const { data } = await api.get("/vehicles/driver/me"); setMyVehicles(data); } catch (err) { log("Failed to fetch vehicles:", err.message); }
  }, []);
  const fetchBookings = useCallback(async () => {
    try { const { data } = await api.get("/bookings"); setBookings(data); } catch (err) { log("Failed to fetch bookings:", err.message); }
  }, []);

  // Auto-refresh bookings on new_booking notification + show toast for TFT events
  useEffect(() => {
    const latest = notifications[0];
    if (!latest) return;
    if (latest.type === "new_booking") {
      fetchBookings();
      toast.success("Nouvelle réservation", { description: latest.message, duration: 6000 });
    } else if (latest.type === "tft_recharge") {
      checkAuth();
      toast.success("Crédit TFT rechargé", { description: latest.message, duration: 5000 });
    } else if (latest.type === "tft_debit") {
      checkAuth();
      toast.info("Commission TFT débitée", { description: latest.message, duration: 4000 });
    } else if (latest.type === "tft_refund") {
      checkAuth();
      toast.success("Commission remboursée", { description: latest.message, duration: 4000 });
    } else if (latest.type === "tft_withdraw") {
      checkAuth();
      toast.warning("Retrait TFT effectué", { description: latest.message, duration: 5000 });
    } else if (latest.type === "tft_recharge_approved") {
      checkAuth();
      toast.success("Recharge approuvée 🎉", { description: latest.message, duration: 7000 });
    } else if (latest.type === "tft_recharge_rejected") {
      toast.error("Recharge rejetée", { description: latest.message, duration: 7000 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, fetchBookings]);

  useEffect(() => {
    fetchMyRides(); fetchMyVehicles(); fetchBookings();
    return () => { if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, [fetchMyRides, fetchMyVehicles, fetchBookings]);

  // React to Express-related global events (fired from ExpressIncomingCallModal
  // or from Service-Worker notification click) so the driver lands directly
  // on the "Courses Express" tab after accepting a call.
  useEffect(() => {
    const onExpressAccepted = () => {
      setTab("express-bookings");
      fetchBookings();
    };
    const onSwitchTab = (e) => {
      const t = e?.detail?.tab;
      if (t) setTab(t);
    };
    window.addEventListener("trigo:express-accepted", onExpressAccepted);
    window.addEventListener("trigo:switch-tab", onSwitchTab);
    // Also honor ?tab=xxx on mount (e.g. after SW notification-click deep link)
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get("tab");
      if (t) setTab(t);
    } catch { /* noop */ }
    return () => {
      window.removeEventListener("trigo:express-accepted", onExpressAccepted);
      window.removeEventListener("trigo:switch-tab", onSwitchTab);
    };
  }, [fetchBookings]);

  const updateBookingStatus = useCallback(async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      // Acknowledge the booking alert so the looped ringtone stops.
      if (typeof window !== "undefined" && typeof window.__trigoAcknowledgeBookingAlert === "function") {
        window.__trigoAcknowledgeBookingAlert(id);
      }
      fetchBookings();
      checkAuth();
    } catch (err) { log("Failed to update booking:", err.message); }
  }, [fetchBookings, checkAuth]);

  const startTracking = useCallback((bookingId) => {
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    setTrackingBookingId(bookingId);
    if (!navigator.geolocation) { alert("Geolocalisation non supportee"); return; }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCurrentLocation(loc);
        api.post("/location/update", { lat: loc.lat, lng: loc.lng, booking_id: bookingId }).catch(() => {});
      },
      (err) => log("Geolocation error:", err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  }, []);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    setTrackingBookingId(null);
    setCurrentLocation(null);
  }, []);

  const tftBalance = user?.tft_balance || 0;
  const isApproved = user?.driver_status === "approved";
  const sortedBookings = useMemo(() => 
    [...bookings].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [bookings]
  );

  // Auto-resume GPS tracking on dashboard load (e.g. after a page refresh)
  // for the most recent booking already in progress.
  useEffect(() => {
    if (trackingBookingId) return;
    const inProgress = bookings.find((bk) => bk.status === "in_progress" && bk.otp_verified);
    if (inProgress && navigator.geolocation) {
      startTracking(inProgress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  // Listen for global "start tracking" events dispatched by the Express modal
  // when the driver accepts a course — tracking must start immediately at
  // status=confirmed so the passenger sees the driver approaching.
  useEffect(() => {
    const onStartEvt = (e) => {
      const bid = e.detail?.bookingId;
      if (bid && navigator.geolocation) startTracking(bid);
    };
    window.addEventListener("trigo:start-tracking", onStartEvt);
    return () => window.removeEventListener("trigo:start-tracking", onStartEvt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="driver-dashboard">
      <div className="bg-[#0A2540] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Bonjour, {user?.name}</h1>
              <p className="text-sm text-white/60">Tableau de bord chauffeur</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <DriverVisibilityToggle />
              <DriverOnlineToggle />
              <NotificationSoundSettings />
              <NotificationsActivateButton />
            </div>
          </div>
        </div>
      </div>

      <DriverStatusBanner status={user?.driver_status} />

      {/* Push activation invite — visible when the driver hasn't enabled
          background push yet. Critical for Express, since a driver who
          doesn't grant notifications misses jobs the moment their tab is
          not focused. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <DriverPushActivationBanner />
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {[
            { label: "Credit TFT", value: `${tftBalance.toLocaleString()} TFT`, danger: tftBalance <= 0 },
            { label: "Mes trajets", value: myRides.length },
            { label: "Mes vehicules", value: myVehicles.length },
            { label: "Reservations", value: bookings.length },
            { label: "Revenus nets", value: `${(user?.total_earnings || 0).toLocaleString()} XAF` },
          ].map((s) => (
            <div key={s.label} className={`bg-white rounded-xl border p-4 ${s.danger ? "border-[#EF4444]/50 bg-[#EF4444]/5" : "border-[#CBD5E1]"}`} data-testid={`stat-${s.label}`}>
              <p className="text-xs text-[#64748B] mb-1">{s.label}</p>
              <p className={`text-lg font-bold ${s.danger ? "text-[#EF4444]" : "text-[#0A2540]"}`} style={{ fontFamily: 'Outfit' }}>{s.value}</p>
              {s.danger && <p className="text-[10px] text-[#EF4444] mt-1">Rechargez pour activer vos services</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
            <TabsList className="bg-white border border-[#CBD5E1] inline-flex w-max sm:w-auto">
              <TabsTrigger value="rides" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-rides"><Car className="w-4 h-4 mr-1" /> Trajets</TabsTrigger>
              <TabsTrigger value="vehicles" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-vehicles"><Truck className="w-4 h-4 mr-1" /> Vehicules</TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-bookings">
                <History className="w-4 h-4 mr-1" /> Reservations
                {unreadCount > 0 && <span className="ml-1 w-4 h-4 bg-[#EF4444] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>}
              </TabsTrigger>
              <TabsTrigger value="express-bookings" className="data-[state=active]:bg-[#F2A93B] data-[state=active]:text-[#0F2B46] text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-express-bookings"><Zap className="w-4 h-4 mr-1" /> Courses Express</TabsTrigger>
              <TabsTrigger value="referral" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-referral"><Gift className="w-4 h-4 mr-1" /> Parrainage</TabsTrigger>
              <TabsTrigger value="tft" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-tft"><Coins className="w-4 h-4 mr-1" /> Mes TFT</TabsTrigger>
              <TabsTrigger value="stats" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-stats"><BarChart3 className="w-4 h-4 mr-1" /> Statistiques</TabsTrigger>
              <TabsTrigger value="pack-report" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-pack-report"><Hourglass className="w-4 h-4 mr-1" /> Rapport Packs</TabsTrigger>
              <TabsTrigger value="profile" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white text-xs sm:text-sm whitespace-nowrap" data-testid="driver-tab-profile"><UserCircle className="w-4 h-4 mr-1" /> Profil</TabsTrigger>
            </TabsList>
          </div>

          {/* Rides */}
          <TabsContent value="rides">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Mes trajets</h2>
              <AddRideDialog open={showRideForm} setOpen={setShowRideForm} disabled={!isApproved} onCreated={fetchMyRides} />
            </div>
            <div className="grid gap-4">
              {myRides.length === 0 ? (
                <p className="text-center py-10 text-[#64748B]">Vous n'avez pas encore de trajet</p>
              ) : myRides.map((ride) => (
                <div key={ride.id} className="bg-white rounded-xl border border-[#CBD5E1] overflow-hidden" data-testid={`my-ride-${ride.id}`}>
                  <div className="flex">
                    {ride.image_url && (
                      <div className="w-24 min-w-[6rem] flex-shrink-0">
                        <img src={resolveImageUrl(ride.image_url)} alt="Trajet" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="flex-1 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-[#0A2540]">{ride.departure} -&gt; {ride.destination}</p>
                      <p className="text-xs text-[#64748B]">{ride.departure_date} a {ride.departure_time} | {ride.booked_seats}/{ride.available_seats} places reservees</p>
                      {ride.pickup_location && (
                        <p className="text-xs text-[#10B981] flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> Prise en charge: {ride.pickup_location}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditRide(ride)} className="h-7 px-2 text-xs text-[#0A2540]" data-testid={`edit-ride-${ride.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#0A2540]">{ride.price_per_seat?.toLocaleString()} XAF/place</p>
                        <span className={`text-xs ${ride.status === "active" ? "text-[#10B981]" : "text-[#64748B]"}`}>{ride.status}</span>
                      </div>
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Vehicles */}
          <TabsContent value="vehicles">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Mes vehicules</h2>
              <AddVehicleDialog open={showVehicleForm} setOpen={setShowVehicleForm} disabled={!isApproved} onCreated={fetchMyVehicles} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {myVehicles.length === 0 ? (
                <p className="text-center py-10 text-[#64748B] col-span-full">Aucun vehicule ajoute</p>
              ) : myVehicles.map((v) => (
                <div key={v.id} className="relative bg-white rounded-xl border border-[#CBD5E1] overflow-hidden" data-testid={`my-vehicle-${v.id}`}>
                  <VehicleStatusStrip status={v.operational_status} />
                  {(v.image_urls?.length > 0 || v.image_url) && (
                    <div className="flex overflow-x-auto">
                      {(v.image_urls?.length > 0 ? v.image_urls : [v.image_url]).filter(Boolean).map((url, i) => (
                        <img key={`${v.id}-img-${i}`} src={resolveImageUrl(url)} alt={`${v.brand} ${v.model} ${i+1}`} className="w-full h-36 object-cover flex-shrink-0" style={{ minWidth: "100%" }} />
                      ))}
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <h3 className="text-sm font-semibold text-[#0A2540] flex-1 min-w-0">{v.brand} {v.model} ({v.year})</h3>
                      <VehicleStatusBadge status={v.operational_status} />
                      <Button size="sm" variant="ghost" onClick={() => setEditVehicle(v)} className="h-7 px-2 text-xs text-[#0A2540]" data-testid={`edit-vehicle-${v.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <p className="text-xs text-[#64748B]">{v.plate_number} | {v.color} | {v.vehicle_type} | {v.seats || 4} places</p>
                    <div className="flex flex-wrap gap-1 mt-1.5 mb-2">
                      {v.air_conditioning && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981]">Clim</span>}
                      {v.music && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FACC15]/20 text-[#0A2540]">Musique</span>}
                      {v.phone_charger && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#0A2540]/10 text-[#0A2540]">Chargeur</span>}
                      {v.fuel_policy === "included" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981]">Carburant inclus</span>}
                    </div>
                    <p className="text-sm font-bold text-[#0A2540]">{v.price_per_day?.toLocaleString()} XAF/jour</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Bookings (rentals only — Express are shown in a dedicated tab) */}
          <TabsContent value="bookings">
            <h2 className="text-lg font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Reservations recues</h2>
            <div className="grid gap-4">
              {sortedBookings.filter(b => b.booking_type !== "express").length === 0 ? (
                <p className="text-center py-10 text-[#64748B]">Aucune reservation</p>
              ) : sortedBookings.filter(b => b.booking_type !== "express").map((b) => (
                <DriverBookingCard
                  key={b.id}
                  booking={b}
                  onUpdate={(...args) => { if (args.length > 0) updateBookingStatus(args[0], args[1]); else { fetchBookings(); checkAuth(); } }}
                  onStartTracking={startTracking}
                  onStopTracking={stopTracking}
                  trackingBookingId={trackingBookingId}
                  currentLocation={currentLocation}
                  currentUserId={user?.id}
                />
              ))}
            </div>
          </TabsContent>

          {/* Trigo Express courses */}
          <TabsContent value="express-bookings">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[#F2A93B]" />
              <h2 className="text-lg font-semibold text-[#0F2B46]" style={{ fontFamily: 'Outfit' }}>Courses Express</h2>
            </div>
            <div className="grid gap-4">
              {/* Fallback-accept queue: missed calls stay grabbable for 1 min 10 s */}
              <ExpressPendingQueue onAccepted={() => { fetchBookings(); checkAuth?.(); }} />
              {sortedBookings.filter(b => b.booking_type === "express").length === 0 ? (
                <p className="text-center py-10 text-[#64748B]">Aucune course Express pour le moment. Activez « Connexion Trigo Express » pour en recevoir.</p>
              ) : sortedBookings.filter(b => b.booking_type === "express").map((b) => (
                <ExpressCourseCard
                  key={b.id}
                  b={b}
                  role="driver"
                  onUpdate={() => { fetchBookings(); checkAuth(); }}
                />
              ))}
            </div>
          </TabsContent>

          {/* Referral */}
          <TabsContent value="referral">
            <ReferralSection user={user} />
          </TabsContent>

          {/* TFT History */}
          <TabsContent value="tft">
            <div className="space-y-6">
              <TFTRecharge onBalanceChange={checkAuth} />
              <TFTHistory endpoint="/driver/tft/history" currentBalance={user?.tft_balance || 0} title="Historique de mes TFT" />
            </div>
          </TabsContent>

          {/* Stats */}
          <TabsContent value="stats">
            <DriverStats />
          </TabsContent>

          {/* Pack bookings report */}
          <TabsContent value="pack-report">
            <PackBookingsReport mode="driver" />
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile">
            <DriverProfile user={user} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialogs */}
      {editRide && <EditRideDialog ride={editRide} open={!!editRide} setOpen={(v) => !v && setEditRide(null)} onUpdated={fetchMyRides} />}
      {editVehicle && <EditVehicleDialog vehicle={editVehicle} open={!!editVehicle} setOpen={(v) => !v && setEditVehicle(null)} onUpdated={fetchMyVehicles} />}
    </div>
  );
}

function DriverStatusBanner({ status }) {
  if (!status || status === "approved") {
    if (status === "approved") return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <div className="rounded-xl border bg-[#10B981]/10 border-[#10B981]/30 p-3 flex items-center gap-2" data-testid="driver-approved-banner">
          <ShieldCheck className="w-5 h-5 text-[#10B981]" />
          <p className="text-sm font-medium text-[#0A2540]">Compte chauffeur verifie</p>
        </div>
      </div>
    );
    return null;
  }
  const isPending = status === "pending";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${isPending ? "bg-[#FACC15]/10 border-[#FACC15]/30" : "bg-[#EF4444]/10 border-[#EF4444]/30"}`} data-testid="driver-status-banner">
        {isPending ? <AlertCircle className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />}
        <div>
          <p className="text-sm font-semibold text-[#0A2540]">{isPending ? "Compte en attente de validation" : "Compte refuse"}</p>
          <p className="text-xs text-[#64748B] mt-1">{isPending ? "Vos documents sont en cours d'examen." : "Votre inscription a ete refusee."}</p>
        </div>
      </div>
    </div>
  );
}

function DriverProfile({ user }) {
  const { checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [silentSaving, setSilentSaving] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showReferralQr, setShowReferralQr] = useState(false);
  const silent = !!user?.silent_notifications;
  const referralCode = user?.referral_code || "";
  const referralUrl = referralCode
    ? `https://tour.trigoride.com/register?ref=${encodeURIComponent(referralCode)}`
    : "https://tour.trigoride.com/register";

  const toggleSilent = async () => {
    setSilentSaving(true);
    try {
      await api.put("/auth/profile", { silent_notifications: !silent });
      await checkAuth();
      toast.success(!silent ? "Mode silencieux activé" : "Mode silencieux désactivé");
    } catch (err) {
      toast.error("Impossible de mettre à jour la préférence");
    } finally { setSilentSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      await checkAuth();
      setEditing(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post("/upload/profile-photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors du telechargement");
    } finally { setUploadingPhoto(false); }
  };

  const docs = user?.driver_documents || {};
  const DRIVER_STATUS = {
    approved: { label: "Approuve", cls: "bg-[#10B981]/10 text-[#10B981]" },
    pending: { label: "En attente", cls: "bg-[#FACC15]/20 text-[#EAB308]" },
    rejected: { label: "Refuse", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  };
  const statusInfo = DRIVER_STATUS[user?.driver_status] || DRIVER_STATUS.rejected;
  const statusText = statusInfo.label;
  const statusCls = statusInfo.cls;

  return (
    <div className="space-y-6" data-testid="driver-profile-section">
      {/* Profile Card */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-6">
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <div className="relative group">
            {user?.profile_photo ? (
              <img src={getAuthImgUrl(user.profile_photo)} alt="Profil" className="w-20 h-20 rounded-xl object-cover border-2 border-[#CBD5E1]" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[#0A2540]/10 flex items-center justify-center border-2 border-[#CBD5E1]">
                <UserCircle className="w-10 h-10 text-[#64748B]" />
              </div>
            )}
            <label className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity" data-testid="upload-profile-photo">
              {uploadingPhoto ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Edit2 className="w-5 h-5 text-white" />}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>
          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{user?.name}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCls}`}>{statusText}</span>
              {(user?.rating > 0) && <ReviewBadge rating={user?.rating} totalReviews={user?.total_reviews} size="md" />}
            </div>
            <div className="space-y-1 text-sm text-[#64748B]">
              <p className="flex items-center gap-2"><Mail className="w-4 h-4" /> {user?.email}</p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> {user?.phone || "Non renseigne"}</p>
              <p className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Code parrainage: <span className="font-mono text-[#0A2540] font-bold">{user?.referral_code || "N/A"}</span></p>
            </div>
          </div>
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => { setForm({ name: user?.name || "", phone: user?.phone || "" }); setEditing(true); }} className="border-[#CBD5E1]" data-testid="edit-profile-btn">
              <Edit2 className="w-3.5 h-3.5 mr-1" /> Modifier
            </Button>
          )}
        </div>

        {/* Edit Form */}
        {editing && (
          <div className="mt-5 pt-5 border-t border-[#CBD5E1] space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#0A2540]">Nom complet</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" data-testid="profile-edit-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A2540]">Telephone</label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" data-testid="profile-edit-phone" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="save-profile-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="border-[#CBD5E1]">Annuler</Button>
            </div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-4 text-center">
          <p className="text-xs text-[#64748B] mb-1">Credit TFT</p>
          <p className={`text-xl font-bold ${(user?.tft_balance || 0) > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`} style={{ fontFamily: 'Outfit' }}>{(user?.tft_balance || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-4 text-center">
          <p className="text-xs text-[#64748B] mb-1">Numero CNI</p>
          <p className="text-sm font-medium text-[#0A2540]">{user?.cni_number || "N/A"}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-4 text-center">
          <p className="text-xs text-[#64748B] mb-1">Numero Permis</p>
          <p className="text-sm font-medium text-[#0A2540]">{user?.license_number || "N/A"}</p>
        </div>
      </div>

      {/* Notifications Preferences */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-6" data-testid="driver-notification-prefs">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#F1F5F9] flex items-center justify-center shrink-0">
            <BellOff className="w-6 h-6 text-[#0A2540]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
              Mode silencieux
            </h3>
            <p className="text-xs text-[#64748B] mt-1">
              Coupe le son et la vibration des messages Flash de l&apos;administration. Les notifications restent visibles à l&apos;écran.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleSilent}
            disabled={silentSaving}
            role="switch"
            aria-checked={silent}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${silent ? "bg-[#0A2540]" : "bg-[#CBD5E1]"} ${silentSaving ? "opacity-60" : ""}`}
            data-testid="driver-silent-mode-toggle"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${silent ? "translate-x-5" : "translate-x-0"}`}
            />
          </button>
        </div>
      </div>

      {/* Aide & Support */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-6" data-testid="driver-support-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#25D366]/10 flex items-center justify-center shrink-0">
            <LifeBuoy className="w-6 h-6 text-[#25D366]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
              Aide & Support
            </h3>
            <p className="text-xs text-[#64748B] mt-1">
              Un problème avec un client, un paiement ou vos documents ? Contactez notre équipe support par WhatsApp.
            </p>
            <p className="text-[11px] text-[#94A3B8] mt-1 font-mono">+{SUPPORT_PHONE.slice(0, 3)} {SUPPORT_PHONE.slice(3, 6)} {SUPPORT_PHONE.slice(6, 9)} {SUPPORT_PHONE.slice(9)}</p>
          </div>
          <a
            href={SUPPORT_WHATSAPP_URL(`Bonjour TrigoTour, je suis chauffeur${user?.name ? " (" + user.name + ")" : ""} et j'ai besoin d'aide.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white text-sm font-bold shadow-sm transition shrink-0"
            data-testid="driver-support-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" /> Contacter
          </a>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-6">
        <h3 className="text-base font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Mes documents</h3>
        <div className="grid grid-cols-2 gap-4">
          <DocCard label="CNI Recto" docPath={docs.cni_front} />
          <DocCard label="CNI Verso" docPath={docs.cni_back} />
          <DocCard label="Permis Recto" docPath={docs.license_front} />
          <DocCard label="Permis Verso" docPath={docs.license_back} />
        </div>
      </div>

      {/* Partager l'application */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] p-6" data-testid="driver-share-app-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FACC15]/20 flex items-center justify-center shrink-0">
            <Share2 className="w-6 h-6 text-[#0A2540]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Partager l&apos;application</h3>
            <p className="text-xs text-[#64748B] mt-1">Invitez vos proches et collègues chauffeurs à essayer TrigoTour.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowShare(true)}
            className="shrink-0 h-10 px-4 rounded-full bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] text-sm font-bold inline-flex items-center gap-1.5"
            data-testid="driver-share-app-btn"
          >
            <Share2 className="w-4 h-4" /> Partager
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShowQr(true)}
          className="mt-3 w-full h-11 rounded-full border border-[#CBD5E1] text-[#0A2540] hover:bg-[#F1F5F9] text-sm font-semibold inline-flex items-center justify-center gap-2"
          data-testid="driver-share-qr-btn"
        >
          <QrCode className="w-4 h-4" /> Afficher le QR code
        </button>
        {referralCode && (
          <button
            type="button"
            onClick={() => setShowReferralQr(true)}
            className="mt-2 w-full h-11 rounded-full bg-[#0A2540] text-[#FACC15] hover:bg-[#081b2e] text-sm font-bold inline-flex items-center justify-center gap-2"
            data-testid="driver-referral-qr-btn"
          >
            <QrCode className="w-4 h-4" /> Mon QR de parrainage · {referralCode}
          </button>
        )}
      </div>
      <ShareQrDialog open={showQr} onOpenChange={setShowQr} />
      <ShareAppDialog open={showShare} onOpenChange={setShowShare} />
      <ReferralStatsCard />
      <ShareQrDialog
        open={showReferralQr}
        onOpenChange={setShowReferralQr}
        url={referralUrl}
        title={`Parrainage · Code ${referralCode}`}
        description={`Faites scanner ce QR pour inscrire de nouveaux clients avec <strong>votre code</strong> — vous gagnez des TFT à chaque nouvel inscrit qui commande.`}
        filename={`trigo-parrainage-${referralCode}`}
      />
    </div>
  );
}

function DocCard({ label, docPath }) {
  if (!docPath) return (
    <div className="border-2 border-dashed border-[#CBD5E1] rounded-lg p-6 text-center">
      <FileText className="w-6 h-6 text-[#CBD5E1] mx-auto mb-1" />
      <p className="text-xs text-[#64748B]">{label} - Non fourni</p>
    </div>
  );
  return (
    <div className="border border-[#CBD5E1] rounded-lg overflow-hidden">
      <p className="text-xs font-medium text-[#64748B] px-3 py-1.5 bg-[#F8FAFC] border-b border-[#CBD5E1]">{label}</p>
      <img src={getAuthImgUrl(docPath)} alt={label} className="w-full h-32 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
    </div>
  );
}
